import React, { useEffect, useRef } from 'react';
import type { Todo } from '@ext-bookmark/shared/types';
import { useQueryClient } from '@tanstack/react-query';
import { useTodoStore } from '../../stores';
import { useGroupStore } from '../../stores';
import { getTodos } from '../../services/todo/storage';
import { mapSupabaseRowToTodo } from '../../services/todo/supabaseStorage';
import { wasTodoJustUpdatedByUs } from '../../services/todo/lastTodoUpdateByUs';
import { subscribeToAll } from '../../services/sync/realtimeSync';
import { startTodoOfflineSync } from '../../services/sync/todoOfflineSyncService';
import { useAuth } from '../../hooks/useAuth';
import { useTodosQuery } from '../../hooks/queries/useTodoQueries';
import { useGroupsQuery } from '../../hooks/queries/useGroupQueries';

interface CalendarContainerProps {
  children: React.ReactNode;
}

/**
 * 日历容器组件，负责数据加载和状态管理
 */
export const CalendarContainer: React.FC<CalendarContainerProps> = ({ children }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { setTodos } = useTodoStore();
  const { setGroups } = useGroupStore();
  const isInitialLoadRef = useRef(true);

  const { data: todosData } = useTodosQuery({ enabled: !!user });
  const { data: groupsData } = useGroupsQuery({ enabled: !!user });

  // 已登录时用 React Query 数据同步到 store
  useEffect(() => {
    if (user && todosData) {
      setTodos(todosData);
    }
  }, [user, todosData, setTodos]);

  useEffect(() => {
    if (user && groupsData) {
      setGroups(groupsData);
    }
  }, [user, groupsData, setGroups]);

  // 未登录时从本地存储加载待办
  useEffect(() => {
    if (user) return;
    getTodos().then(setTodos).catch((err) => console.error('[CalendarContainer] 本地待办加载失败:', err));
  }, [user, setTodos]);

  // 已登录时启动待办离线队列后台同步（网络恢复后重试）
  useEffect(() => {
    if (user) {
      startTodoOfflineSync();
    }
  }, [user]);

  // 订阅实时同步
  useEffect(() => {
    if (!user || !isInitialLoadRef.current) return;

    let unsubscribe: (() => void) | null = null;
    
    subscribeToAll({
      onTodosUpdate: (event, data) => {
        // 局部刷新：只 setQueryData 更新单条，不 invalidate，避免整表 refetch
        if (event === 'UPDATE' && data?.id) {
          if (wasTodoJustUpdatedByUs(data.id)) return;
          const updated = mapSupabaseRowToTodo(data);
          queryClient.setQueryData<Todo[]>(['todos'], (prev) => {
            if (!prev) return [];
            return prev.map((t) => {
              if (t.id !== updated.id) return t;
              if (t.updatedAt != null && updated.updatedAt != null && updated.updatedAt < t.updatedAt) return t;
              return updated;
            });
          });
        } else if (event === 'DELETE' && data?.id) {
          queryClient.setQueryData<Todo[]>(['todos'], (prev) =>
            prev ? prev.filter((t) => t.id !== data.id) : []
          );
        } else if (event === 'INSERT' && data?.id) {
          if (wasTodoJustUpdatedByUs(data.id)) return;
          const inserted = mapSupabaseRowToTodo(data);
          queryClient.setQueryData<Todo[]>(['todos'], (prev) => {
            if (!prev) return [inserted];
            const i = prev.findIndex((t) => t.id === inserted.id);
            if (i >= 0) return prev.map((t, idx) => (idx === i ? inserted : t));
            return [inserted, ...prev];
          });
        }
        // 未知事件静默忽略，不 invalidate，保持单向数据流
      },
      onGroupsUpdate: () => {
        queryClient.invalidateQueries({ queryKey: ['groups'] });
      },
      onMembersUpdate: (_event, data) => {
        queryClient.invalidateQueries({ queryKey: ['groups'] });
        const groupId = data?.group_id;
        if (groupId) {
          queryClient.invalidateQueries({ queryKey: ['groups', groupId, 'members'] });
        }
      },
    }).then((unsub) => {
      unsubscribe = unsub;
    }).catch((error) => {
      console.error('[CalendarContainer] 订阅实时同步失败:', error);
    });

    isInitialLoadRef.current = false;

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [user, queryClient]);

  return <>{children}</>;
};
